import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useContent } from "@/context/ContentContext";

const STYLES = {
  navy: { bg: "bg-gradient-to-br from-[#0f2a5c] via-[#173a7a] to-[#1a4694]", text: "text-white", accent: "text-amber-300", cta: "bg-amber-400 text-[#0f2a5c] hover:bg-white" },
  amber: { bg: "bg-gradient-to-br from-amber-100 via-amber-50 to-white", text: "text-[#0f2a5c]", accent: "text-amber-600", cta: "bg-[#173a7a] text-white hover:bg-[#1a4694]" },
  sky: { bg: "bg-gradient-to-br from-sky-100 via-sky-50 to-white", text: "text-[#0f2a5c]", accent: "text-sky-600", cta: "bg-[#173a7a] text-white hover:bg-[#1a4694]" },
};

const FALLBACK = [
  { slug: "bathroom-tissue-36-rolls", label: "Bathroom Tissue", title: "Comfort Starts Here.", body: "Soft, dependable bathroom tissue designed to bring everyday comfort to your home.", image: "/products/img_07.png", style: "navy" },
  { slug: "kitchen-towel-15-rolls", label: "Kitchen Towels", title: "Soaks Up Every Mess and Spill.", body: "Strong, absorbent and ready for whatever the day brings.", image: "/products/img_13.png", style: "amber" },
  { slug: "facial-tissue-100ct", label: "Facial Tissue", title: "A Softer Touch, Every Time.", body: "Gentle everyday comfort for you and your family.", image: "/products/img_03.png", style: "sky" },
];

export default function ProductBanners() {
  const { content } = useContent();
  const banners = content?.product_banners?.length ? content.product_banners : FALLBACK;

  return (
    <section className="mx-auto max-w-7xl px-5 sm:px-8 py-12 space-y-8 md:space-y-12" data-testid="product-banners" id="banners">
      {banners.map((b, i) => {
        const s = STYLES[b.style] || STYLES.sky;
        const imageSide = i % 2 === 1 ? "left" : "right";
        return (
          <motion.div
            key={b.slug || i}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className={`relative overflow-hidden rounded-[36px] ${s.bg} ${s.text}`}
            data-testid={`banner-${b.slug || i}`}
          >
            <div className={`grid grid-cols-1 md:grid-cols-2 items-center gap-6 md:gap-10 px-8 md:px-14 py-12 md:py-16 ${imageSide === "left" ? "md:[&>div:first-child]:order-2" : ""}`}>
              <div>
                <p className={`text-[11px] uppercase tracking-[0.3em] font-bold ${s.accent}`}>{b.label}</p>
                <h3 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl font-black tracking-tight leading-[1.02]">
                  {b.title}
                </h3>
                <p className="mt-5 max-w-md text-base md:text-lg opacity-80 leading-relaxed">
                  {b.body}
                </p>
                <Link
                  to={`/shop/${b.slug}`}
                  data-testid={`banner-shop-${b.slug}`}
                  className={`mt-8 inline-flex items-center gap-2 rounded-full font-display font-bold px-7 py-3.5 text-sm uppercase tracking-widest transition-colors ${s.cta}`}
                >
                  Shop {b.label} <ArrowRight size={16} />
                </Link>
              </div>
              <div className="relative aspect-square md:aspect-auto md:h-[380px] flex items-center justify-center">
                <img
                  src={b.image}
                  alt={b.label}
                  className="max-h-full max-w-full object-contain drop-shadow-2xl"
                />
              </div>
            </div>
          </motion.div>
        );
      })}
    </section>
  );
}
